import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import TextareaAutosize  from '@mui/material/TextareaAutosize';
import Divider from '@mui/material/Divider';
import EqualizerIcon from '@mui/icons-material/InsertChartOutlined';

import { AutoSaveProvider, useAutoSaveState, SaveButton, NameField } from '../lib/useAutoSaveState'
import { linearRegression } from '../lib/stats'
import toFixed from '../lib/toFixed'
import ScatterPlot from './ScatterPlot'

function LinearRegression() {
  const [groupOneLabel, setGroupOneLabel] = useAutoSaveState('groupOneLabel', 'X')
  const [groupTwoLabel, setGroupTwoLabel] = useAutoSaveState('groupTwoLabel', 'Y')
  const [data1, setData1] = useAutoSaveState('data1', '')
  const [data2, setData2] = useAutoSaveState('data2', '')
  const [ , setPreview ] = useAutoSaveState('preview', null)
  const [result, setResult] = useState({})
  const [points, setPoints] = useState([])
  const { m, b, rSquared, p_value_m } = result

  const calculate = () => {
    const input1 = data1.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    const input2 = data2.split(/[^0-9.-]/).filter(v => v).map(v => parseFloat(v))
    const points = input1.map((x, i) => ({ x, y: input2[i] }))
    console.log(points)
    const output = linearRegression(points)
    output.complete = true
    console.log({ output })
    setPoints(points)
    setResult(output)
    setPreview(`m: ${toFixed(output.m, 5)}, b: ${toFixed(output.b, 5)}, R²: ${toFixed(output.rSquared, 5)}, p: ${toFixed(output.p_value_m, 5)}`)
    window.gtag('event', 'calculate-linear-regression')
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid container justifyContent="center" xs={12} style={{ position: 'relative' }}>
          <Typography variant="h2" component="h1" gutterBottom>
            Linear Regression
          </Typography>
          <SaveButton />
        </Grid>
        <NameField />
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="groupOneLabel"
                  fullWidth
                  id="groupOneLabel"
                  label="X Values"
                  autoFocus
                  value={groupOneLabel}
                  onChange={e => setGroupOneLabel(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data1}
                  onChange={e => setData1(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="groupTwoLabel"
                  fullWidth
                  id="groupTwoLabel"
                  label="Y Values"
                  value={groupTwoLabel}
                  onChange={e => setGroupTwoLabel(e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1">Values</Typography>
                <TextareaAutosize
                  style={{ width: '100%' }}
                  aria-label="data"
                  minRows={3}
                  maxRows={20}
                  placeholder="Enter data..."
                  value={data2}
                  onChange={e => setData2(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Button variant="contained" onClick={calculate}>Calculate</Button>
            </Grid>
          </Paper>
        </Grid>
        {result.complete  && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Grid container xs={12} justifyContent="center">
              <Typography variant="h5" gutterBottom>
                <EqualizerIcon /> Linear Regression Results
              </Typography>
            </Grid>
            <Divider style={{ marginBottom: '20px' }} />
            <Typography
              variant="body1"
              sx={{
                color: p_value_m < 0.05 ? 'green' : 'red',
                textAlign: 'center',
                fontWeight: 'bold'
              }}
            >
              {p_value_m < 0.05 ? 'Significant Results (p < 0.05)' : 'Results Not Significant (p >= 0.05)'}
            </Typography>
            <Divider style={{ marginBottom: '20px', marginTop: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Slope (m) P-Value:</b> {toFixed(p_value_m, 3)}</Typography>
              <Typography variant="body1"><b>Coefficient of Determination (R²):</b> {toFixed(rSquared, 5)}</Typography>
            </Box>
            <Divider style={{ marginBottom: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Slope (m):</b> {toFixed(m, 5)}</Typography>
              <Typography variant="body1"><b>Intercept (b):</b> {toFixed(b, 5)}</Typography>
            </Box>

            <Divider style={{ marginBottom: '20px' }} />
            <Box display="flex" justifyContent="space-around" mb={2}>
              <Typography variant="body1"><b>Equation:</b> y = {toFixed(m, 5)} x + {toFixed(b, 5)}</Typography>
            </Box>
          </Paper>
        </Grid>}
        {result.complete && <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <ScatterPlot data={points} regressionLine={{ m, b }} />
          </Paper>
        </Grid>}
      </Grid>
    </Container>
  );
}


export default function WrappedLinearRegression () {
  return (
    <AutoSaveProvider>
      <LinearRegression />
    </AutoSaveProvider>
  )
}
